import { Value } from 'slate';
import { getSectionKey } from '../../components/userDocuments/editDocument/editResume/EditSpace/EditorPane/RenderContent/showResumeSection';
import { SET_RESUME_INITIAL_STATE } from '../actions/actionTypes/editResumeForm';
import resumeDefaultValues from '../constants/resumeDefaultValues';
import getField from './versions/getField';
import getUserPreferences from './versions/getUserPreferences';
import getUsedAndUnusedSections from './getUsedAndUnusedSections';

export const getEditResumeData = (
  data,
  selectedPlan = null,
  universityPlan = null,
) => {
  const DEFAULT = resumeDefaultValues({ selectedPlan, universityPlan });
  let parsedData = {
    userId: data.userId || DEFAULT.userId,
    templateType: data.templateType || DEFAULT.templateType,
    permissions: data.permissions || DEFAULT.permissions,
    documentName: data.documentName || DEFAULT.documentName,
    status: data.status || DEFAULT.STATUS,
    name: data.name || DEFAULT.NAME,
    resumeType: data.resumeType || DEFAULT.resumeType,
    userPreferences: data.userPreferences
      ? JSON.parse(data.userPreferences)
      : DEFAULT.userPreferences,
    documentPicture: data.documentPicture || null,
    unUsedUrls: data.unUsedUrls || null,
    removeOnNextLoad: data.removeOnNextLoad || { comments: [] },
    showExpertTips: false,
    eventTypes: [],
    editedInputs: DEFAULT.editedInputs,
    isFileImporting: data.isFileImporting || DEFAULT.isFileImporting,
    fileImported: data.fileImported || DEFAULT.fileImported,
    isStreamFinished: data.isStreamFinished || DEFAULT.isStreamFinished,
    importStreamId: data.importStreamId || null,
  };

  /*
   * This is a patch for the user preferences when used or unUsed sections are not present
   * When there is no used orunUsed section in the user preferences, update it to the default used and unUsed
   */
  parsedData = {
    ...parsedData,
    userPreferences: {
      ...getUsedAndUnusedSections({
        userPreferences: parsedData.userPreferences,
        selectedPlan,
        universityPlan,
      }),
    },
  };

  /**
   * User preferences
   * ? Why user preferences is not above name
   */
  const userPreferencesData1 = getUserPreferences(parsedData.userPreferences);
  parsedData = {
    ...parsedData,
    userPreferences: { ...userPreferencesData1 },
  };

  /**
   * UserPreferences
   */

  let userPreferencesData = getField({
    fieldType: 'userPreferences',
    userPreferences: parsedData.userPreferences,
    formatType: parsedData?.templateType,
  });
  userPreferencesData = {
    ...userPreferencesData,
  };
  parsedData = {
    ...parsedData,
    ...userPreferencesData,
  };

  /**
   * Name
   */
  let fullNameData = getField({
    fieldData: data.fullName,
    fieldType: 'fullName',
    nodeType: 'full_name',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  fullNameData = {
    ...fullNameData,
    fullName: Value.fromJSON(fullNameData.fullName),
  };

  parsedData = {
    ...parsedData,
    ...fullNameData,
    userPreferences: parsedData.userPreferences,
  };

  /**
   * Tagline
   */
  let taglineData = getField({
    fieldData: data.tagline,
    fieldType: 'tagline',
    nodeType: 'tagline',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  taglineData = {
    ...taglineData,
    tagline: Value.fromJSON(taglineData.tagline),
  };
  parsedData = {
    ...parsedData,
    ...taglineData,
  };

  /**
   * Phone number
   */
  let phoneNumberData = getField({
    fieldData: data.phoneNumber,
    fieldType: 'phoneNumber',
    nodeType: 'phone_number',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  phoneNumberData = {
    ...phoneNumberData,
    phoneNumber: Value.fromJSON(phoneNumberData.phoneNumber),
  };
  parsedData = {
    ...parsedData,
    ...phoneNumberData,
  };

  /**
   * Email
   */
  let emailData = getField({
    fieldData: data.email,
    fieldType: 'email',
    nodeType: 'email',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  emailData = {
    ...emailData,
    email: Value.fromJSON(emailData.email),
  };
  parsedData = {
    ...parsedData,
    ...emailData,
  };

  /**
   * Address
   */
  let addressData = getField({
    fieldData: data.address,
    fieldType: 'address',
    nodeType: 'address',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  addressData = {
    ...addressData,
    address: Value.fromJSON(addressData.address),
  };
  parsedData = {
    ...parsedData,
    ...addressData,
  };

  /**
   * Objective
   */
  let objectiveData = getField({
    fieldData: data.objective,
    fieldType: 'objective',
    nodeType: 'objective',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });

  objectiveData = {
    ...objectiveData,
    objective: Value.fromJSON(objectiveData.objective),
  };
  parsedData = {
    ...parsedData,
    ...objectiveData,
  };

  /**
   * Summary
   */
  let summaryData = getField({
    fieldData: data.summary,
    fieldType: 'summary',
    nodeType: 'summary',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  summaryData = {
    ...summaryData,
    summary: Value.fromJSON(summaryData.summary),
  };
  parsedData = {
    ...parsedData,
    ...summaryData,
  };

  /**
   * Personal links
   */
  let personalLinksData = getField({
    fieldData: data.personalLinks,
    fieldType: 'personalLinks',
    nodeType: 'personal_link',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  personalLinksData = {
    ...personalLinksData,
    personalLinks: Value.fromJSON(personalLinksData.personalLinks),
  };
  parsedData = {
    ...parsedData,
    ...personalLinksData,
  };

  /**
   * Skills
   */
  let skillsData = getField({
    fieldData: data.skills,
    fieldType: 'skills',
    nodeType: 'skill',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  skillsData = {
    ...skillsData,
    skills: Value.fromJSON(skillsData.skills),
  };
  parsedData = {
    ...parsedData,
    ...skillsData,
  };

  /**
   * Publication
   */
  let publicationsData = getField({
    fieldData: data.publications,
    fieldType: 'publications',
    nodeType: 'publication',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  publicationsData = {
    ...publicationsData,
    publications: Value.fromJSON(publicationsData.publications),
  };
  parsedData = {
    ...parsedData,
    ...publicationsData,
  };

  /**
   * Languages
   */
  let languagesData = getField({
    fieldData: data.languages,
    fieldType: 'languages',
    nodeType: 'language',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  languagesData = {
    ...languagesData,
    languages: Value.fromJSON(languagesData.languages),
  };
  parsedData = {
    ...parsedData,
    ...languagesData,
  };

  /**
   * Hobbies and Interests
   */
  let hobbiesAndInterestsData = getField({
    fieldData: data.hobbiesAndInterests,
    fieldType: 'hobbiesAndInterests',
    nodeType: 'hobbies_and_interests',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  hobbiesAndInterestsData = {
    ...hobbiesAndInterestsData,
    hobbiesAndInterests: Value.fromJSON(
      hobbiesAndInterestsData.hobbiesAndInterests,
    ),
  };
  parsedData = {
    ...parsedData,
    ...hobbiesAndInterestsData,
  };

  /**
   * Work experience
   */
  let workExperienceData = getField({
    fieldData: data.workExperience,
    fieldType: 'workExperience',
    nodeType: 'work_experience_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  workExperienceData = {
    ...workExperienceData,
    workExperience: Value.fromJSON(workExperienceData.workExperience),
  };
  parsedData = {
    ...parsedData,
    ...workExperienceData,
  };

  /**
   * Education
   */
  let educationData = getField({
    fieldData: data.education,
    fieldType: 'education',
    nodeType: 'education_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
    universityPlan,
  });
  educationData = {
    ...educationData,
    education: Value.fromJSON(educationData.education),
  };
  parsedData = {
    ...parsedData,
    ...educationData,
  };

  /**
   * Awards and Honors
   */
  let awardsAndHonorsData = getField({
    fieldData: data.awardsAndHonors,
    fieldType: 'awardsAndHonors',
    nodeType: 'awards_and_honors_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  awardsAndHonorsData = {
    ...awardsAndHonorsData,
    awardsAndHonors: Value.fromJSON(awardsAndHonorsData.awardsAndHonors),
  };
  parsedData = {
    ...parsedData,
    ...awardsAndHonorsData,
  };

  /**
   * Volunteer work
   */
  let volunteerWorkData = getField({
    fieldData: data.volunteerWork,
    fieldType: 'volunteerWork',
    nodeType: 'volunteer_work_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  volunteerWorkData = {
    ...volunteerWorkData,
    volunteerWork: Value.fromJSON(volunteerWorkData.volunteerWork),
  };
  parsedData = {
    ...parsedData,
    ...volunteerWorkData,
  };

  /**
   * Certifications
   */
  let certificationsData = getField({
    fieldData: data.certifications,
    fieldType: 'certifications',
    nodeType: 'certifications_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  certificationsData = {
    ...certificationsData,
    certifications: Value.fromJSON(certificationsData.certifications),
  };
  parsedData = {
    ...parsedData,
    ...certificationsData,
  };

  /**
   * Projects
   */
  let projectsData = getField({
    fieldData: data.projects,
    fieldType: 'projects',
    nodeType: 'projects_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  projectsData = {
    ...projectsData,
    projects: Value.fromJSON(projectsData.projects),
  };
  parsedData = {
    ...parsedData,
    ...projectsData,
  };

  let researchData = getField({
    fieldData: data.researchExperience,
    fieldType: 'researchExperience',
    nodeType: 'research_experience_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  researchData = {
    ...researchData,
    researchExperience: Value.fromJSON(researchData.researchExperience),
  };
  parsedData = {
    ...parsedData,
    ...researchData,
  };

  /**
   * Leadership
   */
  let leadershipData = getField({
    fieldData: data.leadership,
    fieldType: 'leadership',
    nodeType: 'leadership_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  leadershipData = {
    ...leadershipData,
    leadership: Value.fromJSON(leadershipData.leadership),
  };
  parsedData = {
    ...parsedData,
    ...leadershipData,
  };

  /**
   * Associations
   */
  let associationsData = getField({
    fieldData: data.associations,
    fieldType: 'associations',
    nodeType: 'associations_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  associationsData = {
    ...associationsData,
    associations: Value.fromJSON(associationsData.associations),
  };
  parsedData = {
    ...parsedData,
    ...associationsData,
  };

  let extraCurricularData = getField({
    fieldData: data.extraCurricular,
    fieldType: 'extraCurricular',
    nodeType: 'extra_curricular_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  extraCurricularData = {
    ...extraCurricularData,
    extraCurricular: Value.fromJSON(extraCurricularData.extraCurricular),
  };
  parsedData = {
    ...parsedData,
    ...extraCurricularData,
  };

  /**
   * Courses
   */
  let coursesData = getField({
    fieldData: data.courses,
    fieldType: 'courses',
    nodeType: 'courses_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  coursesData = {
    ...coursesData,
    courses: Value.fromJSON(coursesData.courses),
  };
  parsedData = {
    ...parsedData,
    ...coursesData,
  };

  /**
   * Conferences
   */
  let conferencesData = getField({
    fieldData: data.conferences,
    fieldType: 'conferences',
    nodeType: 'conferences_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  conferencesData = {
    ...conferencesData,
    conferences: Value.fromJSON(conferencesData.conferences),
  };
  parsedData = {
    ...parsedData,
    ...conferencesData,
  };

  /**
   * Patents
   */
  let patentsData = getField({
    fieldData: data.patents,
    fieldType: 'patents',
    nodeType: 'patents_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  patentsData = {
    ...patentsData,
    patents: Value.fromJSON(patentsData.patents),
  };
  parsedData = {
    ...parsedData,
    ...patentsData,
  };

  /**
   * Test scores
   */
  let testScoresData = getField({
    fieldData: data.testScores,
    fieldType: 'testScores',
    nodeType: 'test_scores_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  testScoresData = {
    ...testScoresData,
    testScores: Value.fromJSON(testScoresData.testScores),
  };
  parsedData = {
    ...parsedData,
    ...testScoresData,
  };

  /**
   * References
   */
  let referencesData = getField({
    fieldData: data.references,
    fieldType: 'references',
    nodeType: 'references_organization',
    userPreferences: parsedData.userPreferences,
    defaultVal: DEFAULT,
  });
  referencesData = {
    ...referencesData,
    references: Value.fromJSON(referencesData.references),
  };
  parsedData = {
    ...parsedData,
    ...referencesData,
  };

  return parsedData;
};

export default (params) => {
  const { documentData, formatType, documentId, selectedPlan, universityPlan } =
    params;

  const editResumeInitialState = getEditResumeData(
    documentData,
    selectedPlan,
    universityPlan,
  );

  /**
   * TODO: Write logic for personal info separate condition afte completing the section
   */
  const {
    userPreferences: {
      leftPane: { activeElement },
    },
  } = editResumeInitialState;

  return {
    ...editResumeInitialState,
    eventType: SET_RESUME_INITIAL_STATE,
    formatType,
    documentId,
    blobURLs: {},
    saveStatus: true,
    history: {
      tail: {
        data: {
          sectionKey: getSectionKey(activeElement),
        },
        next: null,
        prev: null,
        index: 0,
        changeData: [],
      },
    },
    counter: {},
    isFailed: null,
  };
};
